import React, { useContext, useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useParams } from "react-router-dom";
import { DataGrid, gridPageCountSelector, gridPageSelector, useGridApiContext, useGridSelector } from "@mui/x-data-grid";
import { Pagination, PaginationItem } from "@mui/material";

import { itemsService } from "../services/items.service";
import LogicContext from "../Context/LogicContext";
import AlertConfirm from "../helpers/AlertConfirm";
import { formatCustomNumber } from "../helpers/formatCustomNumber";

// --- Shared Constants ---
const TABLE_COLUMNS = [
  { field: "Rate_Changed_Date", headerName: "Date", flex: 1 },
  { field: "Changed_By_Name", headerName: "Changed By", flex: 1 },
  { field: "Remark", headerName: "Reason", flex: 1.5 },
  { field: "Total_Amount", headerName: "Rate", flex: 1, renderCell: (params) => formatCustomNumber(params.value) },
  { field: "Item_Market_Rate", headerName: "Market Rate", flex: 1, renderCell: (params) => formatCustomNumber(params.value) },
];

// --- Sub-Components ---
const CustomPagination = () => {
  const apiRef = useGridApiContext();
  const page = useGridSelector(apiRef, gridPageSelector);
  const pageCount = useGridSelector(apiRef, gridPageCountSelector);
  return (
    <Pagination
      color="primary" variant="outlined" shape="rounded"
      page={page + 1} count={pageCount}
      renderItem={(props) => <PaginationItem {...props} disableRipple />}
      onChange={(event, value) => apiRef.current.setPage(value - 1)}
    />
  );
};

const HistoryTable = ({ title, rows }) => (
  <div className="bg-white p-4 rounded-lg border border-gray-100 mt-6">
    <h2 className="font-medium text-lg text-gray-800 mb-4">{title}</h2>
    <div style={{ height: 300, width: "100%" }}>
      <DataGrid
        rows={rows}
        columns={TABLE_COLUMNS}
        pageSize={5}
        rowsPerPageOptions={[5]}
        disableSelectionOnClick
        components={{ Pagination: CustomPagination }}
        sx={{
          border: "none",
          "& .MuiDataGrid-columnHeaders": { backgroundColor: "#f9fafb", borderBottom: "1px solid #f3f4f6" },
          "& .MuiDataGrid-cell": { borderBottom: "1px solid #f3f4f6" },
        }}
      />
    </div>
  </div>
);

// --- Main Component ---
function RateHistory({ item_id: propItemId }) {
  const { item_id: paramItemId } = useParams();
  const activeItemId = propItemId || paramItemId;
  
  const logicContext = useContext(LogicContext);
  const [history, setHistory] = useState({ sales: [], cost: [] });
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);

  const { register, handleSubmit, reset, getValues, formState: { errors } } = useForm({
    defaultValues: { Item_Id: activeItemId }
  });

  useEffect(() => {
    if (activeItemId) loadRateHistory();
  }, [activeItemId]);

  const loadRateHistory = async () => {
    try {
      const res = await itemsService.getItemRateHistory(activeItemId);
      const data = res.data || [];
      
      // Grouping logic without underscore
      const formatted = data.reduce((acc, curr, idx) => {
        const item = { ...curr, id: idx }; // DataGrid needs a unique ID
        if (curr.Item_Rate_Type_Id === 1) acc.sales.push(item);
        if (curr.Item_Rate_Type_Id === 2) acc.cost.push(item);
        return acc;
      }, { sales: [], cost: [] });

      setHistory(formatted);
    } catch (err) {
      logicContext.showToast("No Rate history found!", "info");
      setHistory({ sales: [], cost: [] });
    }
  };

  const handleProcessSubmit = (data) => setIsConfirmOpen(true);

  const closeAlert = async (confirmed) => {
    setIsConfirmOpen(false);
    if (!confirmed) return;

    try {
      const payload = { ...getValues(), Item_Id: activeItemId, Option: 1 };
      const res = await itemsService.submitRateHistory(payload);
      logicContext.showToast(res.data?.[0]?.message_text || "Saved successfully", "success");
      reset();
      loadRateHistory();
    } catch (err) {
      logicContext.showToast("Error saving record", "error");
    }
  };

  return (
    <div className="max-w-4xl mx-auto p-4 bg-white min-h-screen">
      {isConfirmOpen && (
        <AlertConfirm title="Save Changes?" body="Are you sure you want to update the rates?" closeAlert={closeAlert} />
      )}

      {/* Form Section */}
      <form onSubmit={handleSubmit(handleProcessSubmit)} className="bg-white p-4 rounded-lg border border-gray-100">
        <h2 className="font-medium text-lg text-gray-800 mb-4">Update Item Rates</h2>
        
        <div className="flex gap-4 mb-4">
          <label className="flex items-center space-x-2 cursor-pointer">
            <input 
              {...register("Item_Rate_Type_Id", { required: "Select a type" })} 
              type="radio" 
              value="1" 
              className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500" 
            />
            <span className="text-sm text-gray-700">Sales Rate</span>
          </label>
          <label className="flex items-center space-x-2 cursor-pointer">
            <input 
              {...register("Item_Rate_Type_Id", { required: "Select a type" })} 
              type="radio" 
              value="2" 
              className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500" 
            />
            <span className="text-sm text-gray-700">Cost Rate</span>
          </label>
          {errors.Item_Rate_Type_Id && <span className="text-xs text-red-500 self-center ml-2">{errors.Item_Rate_Type_Id.message}</span>}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
          <div className="flex flex-col">
            <label className="text-xs text-gray-500 mb-1">New Rate (NGN)</label>
            <input 
              type="number" 
              {...register("Item_Rate", { required: "Required" })} 
              className="p-2 border border-gray-200 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500" 
            />
            {errors.Item_Rate && <span className="text-xs text-red-500 mt-1">{errors.Item_Rate.message}</span>}
          </div>

          <div className="flex flex-col">
            <label className="text-xs text-gray-500 mb-1">Market Rate (NGN)</label>
            <input 
              type="number" 
              {...register("Item_market_rate", { required: "Required" })} 
              className="p-2 border border-gray-200 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500" 
            />
            {errors.Item_market_rate && <span className="text-xs text-red-500 mt-1">{errors.Item_market_rate.message}</span>}
          </div>

          <div className="flex flex-col">
            <label className="text-xs text-gray-500 mb-1">Effective Date</label>
            <input 
              type="date" 
              {...register("Effective_Date", { required: "Required" })} 
              className="p-2 border border-gray-200 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500" 
            />
            {errors.Effective_Date && <span className="text-xs text-red-500 mt-1">{errors.Effective_Date.message}</span>}
          </div>

          <div className="flex flex-col sm:col-span-2">
            <label className="text-xs text-gray-500 mb-1">Remark</label>
            <input 
              type="text" 
              {...register("Rate_Change_Remark", { required: "Required" })} 
              className="p-2 border border-gray-200 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500" 
              placeholder="Reason for change..." 
            />
            {errors.Rate_Change_Remark && <span className="text-xs text-red-500 mt-1">{errors.Rate_Change_Remark.message}</span>}
          </div>
        </div>

        <div className="mt-4 flex justify-end">
          <button 
            type="submit" 
            className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-md font-medium transition-all focus:outline-none focus:ring-1 focus:ring-blue-500"
          >
            Update Rates
          </button>
        </div>
      </form>

      {/* History Tables */}
      <HistoryTable title="Sales Rate History" rows={history.sales} />
      <HistoryTable title="Cost Rate History" rows={history.cost} />
    </div>
  );
}

export default RateHistory;